import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const STATS = [
  {
    id: "missed",
    value: 64,
    suffix: "%",
    label: "Decrease in missed patient enquiries",
    detail:
      "The AI voice receptionist catches calls your front desk can't answer — every ring, every hour.",
  },
  {
    id: "response",
    value: 60,
    prefix: "<",
    suffix: "s",
    label: "Response time to enquiries",
    detail:
      "The AI answers in seconds — even at 2am or during theatre lists — so the next clinic on Google never gets the call.",
  },
  {
    id: "coverage",
    value: 24,
    suffix: "/7",
    label: "Out-of-hours & weekend coverage",
    detail:
      "Continuous voice response across nights, weekends and bank holidays without staffing cost.",
  },
];

function useCountUp(target, durationMs, start) {
  const [n, setN] = useState(0);
  const rafRef = useRef(null);
  useEffect(() => {
    if (!start) return undefined;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * target));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [target, durationMs, start]);
  return n;
}

function StatCell({ stat, inView, index }) {
  const value = useCountUp(stat.value, 1400 + index * 100, inView);
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      data-testid={`roi-stat-${stat.id}`}
      className="relative p-8 sm:p-10 bg-slate-950"
    >
      <div className="flex items-baseline gap-1">
        {stat.prefix && (
          <span className="text-3xl sm:text-4xl text-slate-500 font-heading">
            {stat.prefix}
          </span>
        )}
        <span
          className="font-heading font-semibold text-white tracking-tighter text-5xl sm:text-6xl lg:text-7xl"
          data-testid={`roi-stat-${stat.id}-value`}
        >
          {value}
        </span>
        {stat.suffix && (
          <span className="text-3xl sm:text-4xl text-slate-300 font-heading">
            {stat.suffix}
          </span>
        )}
      </div>
      <p className="mt-5 text-sm uppercase tracking-[0.18em] text-slate-300 font-bold">
        {stat.label}
      </p>
      <p className="mt-3 text-sm text-slate-500 leading-relaxed max-w-xs">
        {stat.detail}
      </p>
    </motion.div>
  );
}

export default function ROIStats() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });

  return (
    <section
      id="roi"
      ref={ref}
      data-testid="roi-section"
      className="relative py-24 sm:py-32 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.3em] uppercase text-slate-500 font-bold mb-6">
              ROI for clinics
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-white">
              Numbers that move the line on the P&amp;L.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-base leading-relaxed text-slate-400">
              Actual results vary by clinic specialty, average treatment value
              and call volume — we model your specific ROI before kick-off.
            </p>
          </div>
        </div>

        <div
          data-testid="roi-grid"
          className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden"
        >
          {STATS.map((s, i) => (
            <StatCell key={s.id} stat={s} inView={inView} index={i} />
          ))}
        </div>

        <p className="mt-8 text-xs text-slate-500 tracking-wide">
          * Indicative ranges based on aggregated industry data and pilot
          deployments. ProCliniq builds a tailored ROI model per clinic before
          deployment.
        </p>
      </div>
    </section>
  );
}
